export const restorationStages = [
  { code: 'arrival', title: 'Chegada & recepção' },
  { code: 'assessment', title: 'Avaliação técnica' },
  { code: 'disassembly', title: 'Desmontagem' },
  { code: 'bodywork', title: 'Funilaria' },
  { code: 'paint', title: 'Pintura' },
  { code: 'mechanics', title: 'Mecânica' },
  { code: 'assembly', title: 'Montagem' },
  { code: 'final-review', title: 'Revisão final' },
  { code: 'delivery', title: 'Entrega' }
] as const

export type ChecklistStatus = 'pending' | 'ok' | 'attention' | 'rejected' | 'notApplicable'
export type UserRole = 'admin' | 'manager' | 'technician' | 'customer'
export type MediaPhase = 'before' | 'during' | 'after'

export { digitsOnly, formatCep, formatCpfCnpj, formatPhone, normalizeCep, normalizeCpfCnpj, normalizePhone } from './customer-fields'

const brlFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })

const MAX_CURRENCY_DIGITS = 12
const MAX_INTEGER_INPUT_DIGITS = 6
const MAX_DECIMAL_INPUT_DIGITS = 6

export function formatCurrency(value: number | string | null | undefined): string {
  const amount = typeof value === 'number' ? value : Number(value ?? 0)
  return brlFormatter.format(Number.isFinite(amount) ? amount : 0).replace(/\u00a0/g, ' ')
}

export function formatCurrencyInput(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, MAX_CURRENCY_DIGITS)
  if (!digits) return ''
  return formatCurrency(Number(digits) / 100)
}

export function parseCurrencyInput(value: string): number | null {
  const digits = value.replace(/\D/g, '').slice(0, MAX_CURRENCY_DIGITS)
  if (!digits) return null
  const amount = Number(digits) / 100
  return Number.isFinite(amount) ? amount : null
}

export function formatIntegerInput(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, MAX_INTEGER_INPUT_DIGITS)
  if (!digits) return ''
  return digits.replace(/^0+(?=\d)/, '')
}

export function parseIntegerInput(value: string): number | null {
  const formatted = formatIntegerInput(value)
  if (!formatted) return null
  const parsed = Number(formatted)
  return Number.isInteger(parsed) ? parsed : null
}

export function formatDecimalInput(value: string): string {
  const raw = value.replace(/\s/g, '').replace(',', '.')
  const separatorIndex = raw.indexOf('.')
  const integerPart = (separatorIndex === -1 ? raw : raw.slice(0, separatorIndex))
    .replace(/\D/g, '')
    .slice(0, MAX_DECIMAL_INPUT_DIGITS)
  const fractionPart = separatorIndex === -1
    ? ''
    : raw.slice(separatorIndex + 1).replace(/\D/g, '').slice(0, 2)
  const integer = integerPart.replace(/^0+(?=\d)/, '')
  if (!integer && !fractionPart && !/[.,]$/.test(value)) return ''
  if (/[.,]$/.test(value)) return `${integer || '0'},`
  return `${integer || '0'}${fractionPart ? `,${fractionPart}` : ''}`
}

export function parseDecimalInput(value: string): number | null {
  const formatted = formatDecimalInput(value).replace(',', '.')
  if (!formatted || formatted.endsWith('.')) return null
  const parsed = Number(formatted)
  return Number.isFinite(parsed) ? parsed : null
}

export function formatQuantity(value: number | string | null | undefined, maximumFractionDigits = 2): string {
  const quantity = typeof value === 'number' ? value : Number(value ?? 0)
  return new Intl.NumberFormat('pt-BR', { maximumFractionDigits }).format(Number.isFinite(quantity) ? quantity : 0)
}

const dateFormatter = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
const dateTimeFormatter = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
})

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—'
  if (typeof value === 'string') {
    const dateOnlyAtUtcMidnight = /^(\d{4}-\d{2}-\d{2})T00:00:00(?:\.000)?Z$/.exec(value)
    if (dateOnlyAtUtcMidnight) return dateFormatter.format(new Date(`${dateOnlyAtUtcMidnight[1]}T00:00:00`))
  }
  const date = parseDate(value)
  return date ? dateFormatter.format(date) : '—'
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—'
  const date = parseDate(value)
  return date ? dateTimeFormatter.format(date) : '—'
}

export function toDateOnly(value: string | Date | null | undefined): string {
  if (!value) return ''
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value
  const date = parseDate(value)
  if (!date) return ''
  return [date.getFullYear(), date.getMonth() + 1, date.getDate()]
    .map((part, index) => index === 0 ? String(part).padStart(4, '0') : String(part).padStart(2, '0'))
    .join('-')
}

export function normalizeDateOnly(value: string): string | null {
  const raw = value.trim()
  const brMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(raw)
  const dateOnly = brMatch ? `${brMatch[3]}-${brMatch[2]}-${brMatch[1]}` : raw
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return null
  const date = new Date(`${dateOnly}T00:00:00Z`)
  return Number.isNaN(date.getTime())
    || date.getUTCFullYear() !== Number(dateOnly.slice(0, 4))
    || date.getUTCMonth() + 1 !== Number(dateOnly.slice(5, 7))
    || date.getUTCDate() !== Number(dateOnly.slice(8, 10))
    ? null
    : dateOnly
}

export function formatDateInput(value: string): string {
  const raw = value.trim()
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw)
  if (isoMatch) return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`
  const digits = raw.replace(/\D/g, '').slice(0, 8)
  if (digits.length <= 2) return digits
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
}

export function parseDateInput(value: string): string | null {
  const dateOnly = normalizeDateOnly(value)
  return dateOnly ? `${dateOnly}T00:00:00.000Z` : null
}

export function dateOnlyToIso(value: string): string | null {
  const dateOnly = normalizeDateOnly(value)
  return dateOnly ? `${dateOnly}T00:00:00.000Z` : null
}

function parseDate(value: string | Date): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const date = new Date(`${value}T00:00:00`)
    return Number.isNaN(date.getTime()) ? null : date
  }
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

export interface VehicleSummary {
  id: string
  brand: string
  model: string
  year: number
  plate: string
}

export interface VehicleDetail extends VehicleSummary {
  vin: string | null
  color: string | null
  version?: number
  customer: { id: string; name: string; email: string; phone: string | null; version?: number }
}

export interface WorkOrderSummary {
  id: string
  number: string
  status: string
  customer: string
  currentStage: string | null
  currentStageSequence: number | null
  stageCount: number
  progress: number
  rejectedItemCount: number
  coverMediaId: string | null
  expectedDelivery: string | null
  vehicle: VehicleSummary
}

export interface ChecklistItemResult {
  id: string
  group: string
  title: string
  status: ChecklistStatus
  note: string | null
  capturedAt: string | null
  version: number
}

export interface ChecklistStageResult {
  id: string
  code: string
  title: string
  sequence: number
  status: string
  progress: number
  completedAt: string | null
  version: number
  items: ChecklistItemResult[]
}

export interface MediaAssetSummary {
  id: string
  fileName: string
  contentType: string
  size: number
  sha256: string
  checklistItemResultId: string | null
  visibleToCustomer: boolean
  phase: MediaPhase
  kind: 'cover' | 'attachment'
  createdAt: string
}

export interface PartLine {
  id: string
  code: string
  description: string
  quantity: number
  unitPrice: number
  version?: number
  active?: boolean
}

export interface LaborEntry {
  id: string
  description: string
  hours: number
  hourlyRate: number
  technicianSubject: string | null
  version?: number
  active?: boolean
}

export interface TimelineEventSummary {
  id: string
  eventType: string
  description: string
  actorSubject: string | null
  visibleToCustomer: boolean
  occurredAt: string
}

// Corpo completo de GET /work-orders/{id} — inclui etapas com itens, peças, mão de obra e
// linha do tempo, usado pelas telas de detalhe (Veículo/Checklist no app do mecânico, OS no
// Operacional, Acompanhamento/Relatório no Portal do Cliente).
export interface WorkOrderDetail {
  id: string
  number: string
  status: string
  vehicleId: string
  vehicle: VehicleDetail
  assignedTechnicianSubject: string | null
  technicianDisplayName: string | null
  coverMediaId: string | null
  expectedDelivery: string | null
  isPublished: boolean
  version: number
  createdAt: string
  stages: ChecklistStageResult[]
  parts: PartLine[]
  labor: LaborEntry[]
  timeline: TimelineEventSummary[]
}

export interface ReportSummary {
  id: string
  revision: number
  variant: ReportVariant
  status: string
  total: number
  requestedAt: string
  generatedAt: string | null
}

export interface ReportApprovalSummary {
  id: string
  subject: string
  documentSha256: string
  ipAddress: string
  userAgent: string
  acceptedAt: string
}

export interface ReportDetail extends ReportSummary {
  workOrderId: string
  objectKey: string | null
  sha256: string | null
  approvals: ReportApprovalSummary[]
}

export interface ReportListItem {
  id: string
  workOrderId: string
  workOrderNumber: string
  vehicleBrand: string
  vehicleModel: string
  revision: number
  variant: ReportVariant
  status: string
  total: number
  requestedAt: string
  generatedAt: string | null
}

export type RepairStatus = 'draft' | 'pendingApproval' | 'approved' | 'rejected'
export type QuoteLineKind = 'part' | 'labor'
export type ReportVariant = 'summary' | 'full' | 'quote'

export interface RepairQuoteLine {
  id: string
  kind: QuoteLineKind
  description: string
  quantity: number
  unitPrice: number
  inventoryItemId?: string | null
  inventoryLotId?: string | null
  inventorySnapshot?: InventoryItemSnapshot | null
}

export interface InventoryItemSnapshot {
  id: string
  code: string
  description: string
  unitPrice: number
}

// Reparo proposto pelo mecânico numa etapa do checklist, com orçamento e decisão do cliente.
// Ver ADR-0011. `total` é a soma das linhas (quantity * unitPrice), projetada pela API.
export interface Repair {
  id: string
  workOrderId: string
  stageExecutionId: string
  title: string
  description: string | null
  status: RepairStatus
  customerDecisionNote: string | null
  submittedForApprovalAt: string | null
  decidedAt: string | null
  version: number
  quoteLines: RepairQuoteLine[]
  total: number
}
